import { useTranslation } from 'react-i18next';
import {
  CheckCircle2,
  CircleSlash,
  Loader2,
  TriangleAlert
} from 'lucide-react';
import Button from '@components/atoms/ui-kit/button.tsx';
import { Text } from '@components/atoms/ui-kit/text.tsx';
import {
  isActiveIdentificationStatus,
  VoiceIdentificationStatus
} from '@utils/types/organization/voice-profile.ts';
import { VoiceErrorMessage } from './voice-error-message.tsx';

interface Props {
  status: VoiceIdentificationStatus;
  reason: string | null;
  /** Hidden when the session cannot be processed (not finished, not retained). */
  canRerun: boolean;
  onRerun: () => void;
  isRerunning?: boolean;
  error?: unknown;
}

/** All six recognition states, each with its own next step.
 *
 * A rerun is always explicit. The backend keeps human decisions for labels
 * that still exist, so rerunning after enrolling a new person or fixing a
 * failure is safe — and skipped sessions are never retried automatically, so
 * this button is the only way to run them again. */
export const RecognitionStatus = ({
  status,
  reason,
  canRerun,
  onRerun,
  isRerunning = false,
  error
}: Props) => {
  const { t } = useTranslation();

  const active = isActiveIdentificationStatus(status);

  const icon = () => {
    if (active)
      return <Loader2 size={14} className="animate-spin" aria-hidden />;
    if (status === 'completed') return <CheckCircle2 size={14} aria-hidden />;
    if (status === 'failed') return <TriangleAlert size={14} aria-hidden />;
    if (status === 'skipped') return <CircleSlash size={14} aria-hidden />;
    return null;
  };

  // A rerun is pointless while one is already queued or running.
  const rerunLabel =
    status === 'not_requested'
      ? t('voice-run-recognition')
      : t('voice-rerun-recognition');

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        {icon()}
        <Text as="p" size="sm">
          {t(`voice-status-${status}`)}
        </Text>
      </div>

      {/* The backend's own reason for a skip or failure, when it gave one. */}
      {reason && (status === 'skipped' || status === 'failed') && (
        <Text as="p" size="xs" tone="muted">
          {t('voice-status-reason', { reason })}
        </Text>
      )}

      {/* `aria-live` so a screen reader hears the job reaching a terminal
          state without the user polling the panel themselves. */}
      <span className="sr-only" aria-live="polite">
        {t(`voice-status-${status}`)}
      </span>

      {canRerun && (
        <Button
          variant="secondary"
          size="small"
          label={rerunLabel}
          onClick={onRerun}
          loading={isRerunning}
          disabled={isRerunning || active}
        />
      )}

      <VoiceErrorMessage error={error} onRetry={onRerun} />
    </div>
  );
};
